import React from 'react';
import { Group, Paper, Autocomplete, Button, ActionIcon, Box, Text } from '@mantine/core';
import { IconPlus, IconListCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Z_INDEX_MOBILE_MODAL_DATEPICKER } from '@/constants/zIndex';
import classes from './RequirementInput.module.css';

interface RequirementInputProps {
  value: string;
  onChange: (value: string) => void;
  onAdd: () => void;
  onKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  autocompleteData: Array<{
    value: string;
    label: string;
    category?: string;
    icon?: React.ComponentType<{ size?: number; stroke?: number; color?: string; className?: string; }>;
  }>;
  categoryColors: Record<string, string>;
}

export const RequirementInput: React.FC<RequirementInputProps> = ({
  value,
  onChange,
  onAdd,
  onKeyPress,
  disabled = false,
  autocompleteData,
  categoryColors,
}) => {
  const { t } = useTranslation();

  // Custom render function for requirement suggestions
  const renderRequirementOption = ({ option }: { option: { value: string; label?: string; category?: string; icon?: React.ComponentType<{ size?: number; stroke?: number; color?: string; className?: string; }> } }) => {
    const suggestion = option as { value: string; category?: string; icon?: React.ComponentType<{ size?: number; stroke?: number; color?: string; className?: string; }> };
    const IconComponent = suggestion.icon;
    const iconColor = suggestion.category ? categoryColors[suggestion.category] || 'var(--mantine-color-gray-5)' : 'var(--mantine-color-gray-5)';

    return (
      <Group gap="sm" w="100%" className={classes.suggestionOption}>
        {IconComponent && <IconComponent size={16} stroke={1.5} color={iconColor} />}
        <Box className={classes.suggestionContent}>
          <Text size="sm" lineClamp={1}>{suggestion.value}</Text>
          <Text size="xs" c="dimmed">{suggestion.category}</Text>
        </Box>
      </Group>
    );
  };

  return (
    <Paper className={classes.inputPaper} shadow="0">
      <Group>
        <Autocomplete
          placeholder={t('wizard.step3.typeRequirement')}
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          disabled={disabled}
          className={classes.autocompleteInput}
          leftSection={<IconListCheck size={16} />}
          data={autocompleteData}
          renderOption={renderRequirementOption}
          comboboxProps={{
            withinPortal: true,
            zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
            dropdownPadding: 8
          }}
          maxDropdownHeight={300}
          limit={8}
          autoComplete="off"
          filter={({ options, search }) => {
            const searchLower = search.toLowerCase().trim();
            return options.filter((item) => {
              // Check if item has a 'value' property (it's a regular option, not a group)
              if ('value' in item && typeof item.value === 'string') {
                const optionItem = item as { value: string; category?: string };
                return optionItem.value.toLowerCase().includes(searchLower) ||
                  (optionItem.category && optionItem.category.toLowerCase().includes(searchLower));
              }
              return false;
            });
          }}
        />

        {/* Icon-only button - shown on mobile OR when container is narrow */}
        <ActionIcon
          onClick={onAdd}
          disabled={!value.trim() || disabled}
          size="lg"
          aria-label={t('wizard.step3.addRequirement')}
          className={classes.addButtonIcon}
        >
          <IconPlus size={16} />
        </ActionIcon>

        {/* Full button with text - shown on desktop when container is wide */}
        <Button
          onClick={onAdd}
          disabled={!value.trim() || disabled}
          leftSection={<IconPlus size={16} />}
          className={classes.addButtonFull}
        >
          {t('wizard.step3.addRequirement')}
        </Button>
      </Group>
    </Paper>
  );
};